import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wrench, AlertTriangle, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  ["booth","Booth Install"],["graphics","Graphics"],["artwork","Outstanding Artwork"],
  ["electrical","Electrical"],["furniture","Furniture"],["service","Service Ticket"],
  ["staffing","On-site Staff"],["vendor","Vendor Comms"],["emergency","Emergency"],
];

export default function Operations() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ category: "booth", title: "", body: "", priority: "normal", booth_number: "" });

  useEffect(() => {
    if (loading) return;
    if (!user || (user.role !== "admin" && user.role !== "tl_internal")) { nav("/"); return; }
    api.get("/admin/ops-tickets").then(({ data }) => setItems(data.tickets));
  }, [user, loading]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/ops-tickets", form);
      setItems((x) => [data.ticket, ...x]);
      setForm({ category: "booth", title: "", body: "", priority: "normal", booth_number: "" });
      toast.success("Ticket created.");
    } catch (e) { toast.error("Failed"); }
  };

  const updateStatus = async (id, status) => {
    const { data } = await api.put(`/admin/ops-tickets/${id}`, { status });
    setItems((x) => x.map((t) => t.id === id ? data.ticket : t));
  };

  const remove = async (id) => {
    await api.delete(`/admin/ops-tickets/${id}`);
    setItems((x) => x.filter((t) => t.id !== id));
  };

  const counts = { open: items.filter(t => t.status === "open").length, in_progress: items.filter(t => t.status === "in_progress").length, resolved: items.filter(t => t.status === "resolved").length };

  return (
    <Layout>
      <section className="container-summit py-12 md:py-16">
        <div className="overline mb-4"><span className="accent-rule" />Live Operations Center · Display America</div>
        <h1 className="text-4xl sm:text-5xl">The operational heartbeat.</h1>
        <p className="mt-3 text-zinc-500 max-w-2xl">A single board for everything happening on the ground — install, artwork, vendor comms, emergencies.</p>

        <div className="mt-10 grid grid-cols-3 gap-px bg-[hsl(var(--line))]">
          {[["Open", counts.open], ["In progress", counts.in_progress], ["Resolved", counts.resolved]].map(([l, n]) => (
            <div key={l} className="bg-white p-5">
              <div className="serif text-3xl">{n}</div>
              <div className="overline mt-1">{l}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 grid lg:grid-cols-[1fr_2fr] gap-6">
          <form onSubmit={create} className="card-flat p-6 grid gap-4 self-start">
            <h2 className="text-lg">New ticket</h2>
            <div><Label className="overline">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="h-11 rounded-none mt-2"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Input placeholder="Title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-11 rounded-none" />
            <Textarea placeholder="Details" rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="rounded-none" />
            <Input placeholder="Booth # (optional)" value={form.booth_number} onChange={(e) => setForm({ ...form, booth_number: e.target.value })} className="h-11 rounded-none" />
            <div><Label className="overline">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="h-11 rounded-none mt-2"><SelectValue /></SelectTrigger>
                <SelectContent>{["low","normal","high","urgent"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <button className="btn-black" data-testid="ops-create"><Plus size={14} /> Create Ticket</button>
          </form>
          <div className="grid gap-3">
            {items.map((t) => (
              <div key={t.id} className={`card-flat p-5 ${t.priority === "urgent" ? "border-red-500" : ""}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="pill">{t.category}</span>
                      <span className={`pill ${t.priority === "urgent" ? "bg-red-50 border-red-500 text-red-700" : t.priority === "high" ? "bg-zinc-900 text-white border-zinc-900" : ""}`}>{t.priority}</span>
                      <span className="text-xs text-zinc-500">{t.booth_number ? `Booth ${t.booth_number}` : ""}</span>
                    </div>
                    <div className="text-base font-medium mt-2">{t.title}</div>
                    {t.body && <p className="text-sm text-zinc-600 mt-2">{t.body}</p>}
                  </div>
                  <button onClick={() => remove(t.id)} className="text-zinc-400 hover:text-black"><Trash2 size={14} /></button>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {["open","in_progress","resolved"].map(st => (
                    <button key={st} onClick={() => updateStatus(t.id, st)}
                      className={`px-3 py-1 text-xs uppercase tracking-[0.18em] ${t.status === st ? "bg-black text-white" : "border border-zinc-300"}`}>
                      {st.replace("_"," ")}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {items.length === 0 && <div className="card-flat p-10 text-center text-zinc-500 text-sm">No tickets yet.</div>}
          </div>
        </div>
      </section>
    </Layout>
  );
}
